'use client'

import { useMemo } from 'react'

/**
 * Global error boundary (rendered when the root layout itself throws).
 * Must render its own <html> + <body>. Deliberately minimal — no
 * external scripts, no third-party CDN, no raw error output.
 *
 * Previous implementation injected `//cdn.jsdelivr.net/npm/eruda` to
 * expose an in-browser devtools panel and dumped
 * `JSON.stringify(error, Object.getOwnPropertyNames(error))` to the
 * page. Both patterns were removed per the 2026-04-21
 * production-readiness audit — they exposed internal state + created
 * a supply-chain attack surface on a page that runs with no CSP
 * (because it replaces the root layout).
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const reference = useMemo(() => computeReference(error), [error])

  return (
    <html lang="en">
      <body>
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Critical Error
            </h1>
            <p className="text-gray-600 mb-6">
              A critical error occurred. Please refresh the page or contact
              support if the problem persists.
            </p>
            <p className="text-xs text-gray-500 font-mono mb-6">
              Reference: <span className="select-all">{reference}</span>
            </p>
            <button
              onClick={reset}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </body>
    </html>
  )
}

function computeReference(error: Error & { digest?: string }): string {
  if (error?.digest && error.digest.length > 0) {
    return error.digest.slice(0, 16).toLowerCase()
  }
  const msg = error?.message || 'unknown'
  let hash = 5381
  for (let i = 0; i < msg.length; i++) {
    hash = ((hash << 5) + hash + msg.charCodeAt(i)) | 0
  }
  return `ge-${(hash >>> 0).toString(16).padStart(8, '0')}`
}
